// src/contexts/EnduserAuthContext.tsx - תיקון מלא

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { enduserAuthService } from "../services/enduser/enduserAuthService";

interface User {
  id: number;
  name: string;
  email: string;
  phone_number: string;
  user_type: "personal" | "commercial";
  company_name?: string;
  verified: boolean;
  free_access?: boolean;
  last_login?: string;
}

interface SubscriptionStatus {
  status: "active" | "trial" | "expired";
  type: string;
  can_calculate: boolean;
  can_view_history: boolean;
  expires_at?: string;
  trial_expires_at?: string;
  message: string;
  // ===== ניסיון חינם בעזרת קוד אימות (מאי 2026) =====
  // השדות חוזרים מ-checkUserSubscription גם כשהניסיון פעיל (type:'free_trial')
  // וגם כשאין מנוי (type:'no_subscription') - לצורך badge, פופאפ וחיבור ל-paywall.
  free_trial_activated?: boolean;
  free_trial_used?: number;
  free_trial_max?: number;
  free_trial_remaining?: number;
  // ===== קופון גישה חופשית (מאי 2026) =====
  // חוזרים מ-checkUserSubscription כש-type:'coupon' (גישת קופון פעילה).
  // expires_at משמש כ-window_end להצגת "נותרו X שעות".
  coupon_id?: number;
  coupon_code?: string;
  coupon_campaign_name?: string;
}

interface EnduserAuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  subscriptionStatus: SubscriptionStatus | null;
  sessionToken: string | null;
  isLoading: boolean;
  authError: string | null;

  // Auth functions
  login: (
    email: string
  ) => Promise<{ success: boolean; message: string; data?: any }>;
  register: (
    data: any
  ) => Promise<{ success: boolean; message: string; data?: any }>;
  verifyEmail: (
    code: string
  ) => Promise<{ success: boolean; message: string; data?: any }>;
  logout: () => void;

  // Session functions
  refreshAuth: () => Promise<boolean>;
  verifySession: () => Promise<boolean>;

  // Subscription helpers
  canAccessCalculators: () => boolean;
  canViewHistory: () => boolean;
  getSubscriptionMessage: () => string;

  // Manual setters
  setUser: (user: User | null) => void;
}

const EnduserAuthContext = createContext<EnduserAuthContextType | undefined>(
  undefined
);

interface EnduserAuthProviderProps {
  children: ReactNode;
}

export const EnduserAuthProvider: React.FC<EnduserAuthProviderProps> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] =
    useState<SubscriptionStatus | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [authError, setAuthError] = useState<string | null>(null);

  // Initialize auth state from cookie
  useEffect(() => {
    const initAuth = async () => {
      try {
        setIsLoading(true);
        setAuthError(null);

        const token = enduserAuthService.getSessionToken();
        // console.log("🍪 Session token found:", !!token);

        if (token) {
          setSessionToken(token);

          // FIXED: Call the actual verify_session API
          // console.log("🔍 Verifying session with backend...");
          const isValid = await verifySession();
          if (!isValid) {
            // console.log("❌ Session invalid, clearing auth");
            clearAuth();
          } else {
            // console.log("✅ Session valid, user authenticated");
            setIsAuthenticated(true);
          }
        } else {
          // console.log("❌ No session token found");
          clearAuth();
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
        setAuthError("שגיאה באתחול המערכת");
        clearAuth();
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const clearAuth = () => {
    setIsAuthenticated(false);
    setUser(null);
    setSubscriptionStatus(null);
    setSessionToken(null);
    setAuthError(null);
  };

  // FIXED: Real session verification with API call
  const verifySession = async (): Promise<boolean> => {
    try {
      // console.log("🔍 Calling verify_session API...");
      const result = await enduserAuthService.verifySession();

      // console.log("📥 verify_session result:", result);

      if (result.res === "OK" && result.data) {
        // console.log("✅ Session verification successful");
        // console.log("👤 User data:", result.data.user_info);
        // console.log("📋 Subscription status:", result.data.subscription_status);

        // Set user data
        setUser(result.data.user_info);
        setSubscriptionStatus(result.data.subscription_status);
        setIsAuthenticated(true);
        setAuthError(null);

        // CRITICAL: Log free_access status
        // console.log("🔑 User free_access:", result.data.user_info.free_access);

        return true;
      } else {
        // console.log("❌ Session verification failed:", result.msg);
        setAuthError(result.msg);
        return false;
      }
    } catch (error) {
      console.error("Session verification error:", error);
      setAuthError("שגיאה באימות הסשן");
      return false;
    }
  };

  const refreshAuth = async (): Promise<boolean> => {
    const token = enduserAuthService.getSessionToken();
    setSessionToken(token);

    if (token) {
      return await verifySession();
    } else {
      clearAuth();
      return false;
    }
  };

  // רענון יתרת ניסיון חינם בזמן אמת: מאזין לאירוע שנשלח אחרי כל חישוב סופי מוצלח
  // (מ-calculatorService) ומרענן את subscriptionStatus, כך שה-badge "נותרו X/5"
  // יתעדכן מיד גם בתוך המחשבון ולא רק בחזרה לדשבורד.
  useEffect(() => {
    const handleSubscriptionRefresh = () => {
      if (enduserAuthService.getSessionToken()) {
        verifySession();
      }
    };

    window.addEventListener("cartax:subscription-refresh", handleSubscriptionRefresh);
    return () => {
      window.removeEventListener("cartax:subscription-refresh", handleSubscriptionRefresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auth functions (unchanged)
  const login = async (email: string) => {
    try {
      setIsLoading(true);
      setAuthError(null);

      const result = await enduserAuthService.login({ email });

      if (result.res === "OK") {
        const token = enduserAuthService.getSessionToken();
        setSessionToken(token);

        return {
          success: true,
          message: result.msg,
          data: result.data,
        };
      } else {
        setAuthError(result.msg);
        return {
          success: false,
          message: result.msg || "שגיאה בהתחברות",
        };
      }
    } catch (error) {
      console.error("Login error:", error);
      const errorMsg = "שגיאה בהתחברות";
      setAuthError(errorMsg);
      return {
        success: false,
        message: errorMsg,
      };
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (data: any) => {
    try {
      setIsLoading(true);
      setAuthError(null);

      const result = await enduserAuthService.register(data);

      if (result.res === "OK") {
        const token = enduserAuthService.getSessionToken();
        setSessionToken(token);

        return {
          success: true,
          message: result.msg,
          data: result.data,
        };
      } else {
        setAuthError(result.msg);
        return {
          success: false,
          message: result.msg || "שגיאה ברישום",
        };
      }
    } catch (error) {
      console.error("Registration error:", error);
      const errorMsg = "שגיאה ברישום";
      setAuthError(errorMsg);
      return {
        success: false,
        message: errorMsg,
      };
    } finally {
      setIsLoading(false);
    }
  };

  const verifyEmail = async (code: string) => {
    try {
      setIsLoading(true);
      setAuthError(null);

      const token = sessionToken || enduserAuthService.getSessionToken();

      if (!token) {
        const errorMsg = "לא נמצא טוקן הפעלה";
        setAuthError(errorMsg);
        return {
          success: false,
          message: errorMsg,
        };
      }

      const result = await enduserAuthService.verifyEmail({
        session_token: token,
        verification_code: code,
      });

      if (result.res === "OK") {
        // FIXED: After successful verification, load user data
        await verifySession();

        return {
          success: true,
          message: result.msg,
          data: result.data,
        };
      } else {
        setAuthError(result.msg);
        return {
          success: false,
          message: result.msg || "שגיאה באימות",
        };
      }
    } catch (error) {
      console.error("Verification error:", error);
      const errorMsg = "שגיאה באימות";
      setAuthError(errorMsg);
      return {
        success: false,
        message: errorMsg,
      };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    enduserAuthService.logout();
    clearAuth();
  };

  // FIXED: Subscription helper functions with proper free_access check
  const canAccessCalculators = (): boolean => {
    // console.log("🔍 Checking calculator access...");
    // console.log("🔍 User:", user);
    // console.log("🔍 User free_access:", user?.free_access);
    // console.log("🔍 Subscription status:", subscriptionStatus);

    // PRIORITY 1: אם יש free_access - גישה מלאה
    if (user?.free_access === true) {
      // console.log("✅ User has FREE ACCESS - allowing calculators");
      return true;
    }

    // PRIORITY 2: בדיקה לפי מנוי
    if (!subscriptionStatus) {
      // console.log("❌ No subscription status found");
      return false;
    }

    const hasAccess = subscriptionStatus.can_calculate === true;
    // console.log(
    //   "🔍 Subscription can_calculate:",
    //   subscriptionStatus.can_calculate
    // );
    // console.log(
    //   hasAccess
    //     ? "✅ Calculator access granted via subscription"
    //     : "❌ Calculator access denied"
    // );

    return hasAccess;
  };

  const canViewHistory = (): boolean => {
    // console.log("🔍 Checking history access...");

    // Free access users can always view history
    if (user?.free_access === true) {
      // console.log("✅ History access granted (free access)");
      return true;
    }

    // היסטוריה תמיד זמינה גם ללא מנוי
    if (!subscriptionStatus) {
      // console.log("✅ History access granted (no subscription check)");
      return true;
    }

    const hasAccess = subscriptionStatus.can_view_history === true;
    // console.log(
    //   "🔍 Subscription can_view_history:",
    //   subscriptionStatus.can_view_history
    // );
    // console.log(
    //   hasAccess ? "✅ History access granted" : "❌ History access denied"
    // );

    return hasAccess;
  };

  const getSubscriptionMessage = (): string => {
    // console.log("🔍 Getting subscription message...");

    if (user?.free_access === true) {
      return "גישה חופשית פעילה 🎉";
    }

    if (!subscriptionStatus) {
      return "לא נמצא מידע על מנוי";
    }

    const message = subscriptionStatus.message || "מידע על מנוי לא זמין";
    // console.log("🔍 Subscription message:", message);
    return message;
  };

  const value: EnduserAuthContextType = {
    isAuthenticated,
    user,
    subscriptionStatus,
    sessionToken,
    isLoading,
    authError,

    login,
    register,
    verifyEmail,
    logout,

    refreshAuth,
    verifySession,

    canAccessCalculators,
    canViewHistory,
    getSubscriptionMessage,

    setUser,
  };

  return (
    <EnduserAuthContext.Provider value={value}>
      {children}
    </EnduserAuthContext.Provider>
  );
};

export const useEnduserAuth = (): EnduserAuthContextType => {
  const context = useContext(EnduserAuthContext);
  if (context === undefined) {
    throw new Error(
      "useEnduserAuth must be used within an EnduserAuthProvider"
    );
  }
  return context;
};
